import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronRight, RefreshCcw, ShieldCheck, ShoppingBag } from "lucide-react";

import { Button } from "@/components/ui/button";

const GOLD = "#d4af37";
const CART_STORAGE_KEY = "numeroloji_cart_v1";

function clearCart() {
  try {
    globalThis?.localStorage?.setItem(CART_STORAGE_KEY, JSON.stringify([]));
  } catch {
    // ignore
  }
}

type OrderStatus = "pending" | "paid" | "completed" | "cancelled";

type OrderResponse = {
  ok: boolean;
  order: {
    id: string;
    status: OrderStatus;
    totalTry: number;
    createdAt: number;
    paidAt: number | null;
    packageCount: number;
    productCount: number;
    isDigital: boolean;
    isMembership: boolean;
    payment: null | {
      provider: "iyzico";
      paymentPageUrl: string | null;
      lastStatus: string | null;
      lastError: string | null;
      lastCheckedAt: number | null;
    };
  };
  settings: {
    payment: {
      ibanLabel: string;
      iban: string;
      accountName: string;
      note: string;
    };
  };
};

type IyzicoInitResponse = {
  ok: boolean;
  token: string;
  paymentPageUrl: string;
  checkoutFormContent: string | null;
  alreadyPaid?: boolean;
};

type IyzicoCheckResponse = {
  ok: boolean;
  alreadyPaid?: boolean;
  status?: string;
  error?: string | null;
};

type FreeCompleteResponse = {
  ok: boolean;
  alreadyPaid?: boolean;
  error?: string;
};

async function fetchOrder(id: string): Promise<OrderResponse> {
  let res: Response;
  try {
    res = await fetch(`/api/numeroloji/orders/${encodeURIComponent(id)}`);
  } catch {
    throw new Error("Bağlantı hatası (Failed to fetch). Sayfayı yenileyip tekrar deneyin.");
  }

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Sipariş bulunamadı.");
  }
  return json as OrderResponse;
}

async function initIyzico(input: { orderId: string; token: string }): Promise<IyzicoInitResponse> {
  let res: Response;
  try {
    res = await fetch("/api/numeroloji/iyzico/initialize", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, returnOrigin: window.location.origin }),
    });
  } catch {
    throw new Error("Bağlantı hatası (Failed to fetch). Tekrar deneyin.");
  }

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ödeme başlatılamadı.");
  }
  return json as IyzicoInitResponse;
}

async function checkIyzico(input: { orderId: string; token: string }): Promise<IyzicoCheckResponse> {
  let res: Response;
  try {
    res = await fetch("/api/numeroloji/iyzico/check", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  } catch {
    throw new Error("Bağlantı hatası (Failed to fetch). Tekrar deneyin.");
  }

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ödeme durumu kontrol edilemedi.");
  }
  return json as IyzicoCheckResponse;
}

async function completeFreeCheckout(input: { orderId: string; token: string }): Promise<FreeCompleteResponse> {
  let res: Response;
  try {
    res = await fetch("/api/numeroloji/orders/free-complete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
  } catch {
    throw new Error("Bağlantı hatası (Failed to fetch). Tekrar deneyin.");
  }

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Ücretsiz ödeme tamamlanamadı.");
  }
  return json as FreeCompleteResponse;
}

function formatTry(amount: number) {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ₺`;
  }
}

export default function NumerolojiOdeme() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orderId = useMemo(() => (searchParams.get("orderId") ?? "").trim(), [searchParams]);
  const token = useMemo(() => (searchParams.get("token") ?? "").trim(), [searchParams]);
  const packageKey = useMemo(() => (searchParams.get("package") ?? "").trim(), [searchParams]);
  const reportLang = useMemo(() => (searchParams.get("lang") ?? "tr").trim(), [searchParams]);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["numeroloji-order", orderId],
    queryFn: () => fetchOrder(orderId),
    enabled: Boolean(orderId),
  });

  const tokenInvalid = useMemo(() => {
    const e = String(data?.order.payment?.lastError ?? "");
    return /(^|\D)5122(\D|$)/.test(e) || /token.*bulunamad/i.test(e) || /ödeme bilgisi bulunamad/i.test(e);
  }, [data?.order.payment?.lastError]);

  // If payment confirmed, send user to analysis info page.
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (redirectedRef.current) return;
    if (!data || data.order.status !== "paid") return;
    if ((Number(data.order.packageCount) || 0) <= 0) return;

    redirectedRef.current = true;
    clearCart();
    const url = new URL("/numeroloji/form", window.location.origin);
    url.searchParams.set("orderId", orderId);
    url.searchParams.set("token", token);
    if (packageKey) url.searchParams.set("package", packageKey);
    url.searchParams.set("lang", reportLang);
    navigate(url.pathname + url.search, { replace: true });
  }, [data, navigate, orderId, packageKey, reportLang, token]);

  const iyzicoMutation = useMutation({
    mutationFn: initIyzico,
    onSuccess: (resp) => {
      if (resp.alreadyPaid) return;
      if (resp.paymentPageUrl) window.location.href = resp.paymentPageUrl;
    },
  });

  const iyzicoCheckMutation = useMutation({
    mutationFn: checkIyzico,
    onSuccess: async () => {
      await refetch();
    },
  });

  const freeCheckoutEnabled = String(import.meta.env.VITE_NUMEROLOJI_FREE_CHECKOUT_ENABLED ?? "").trim() === "true";

  const freeCheckoutMutation = useMutation({
    mutationFn: completeFreeCheckout,
    onSuccess: async () => {
      await refetch();
    },
  });

  const freeAttemptedRef = useRef(false);
  useEffect(() => {
    if (!freeCheckoutEnabled) return;
    if (freeAttemptedRef.current) return;
    if (!orderId || !token) return;
    if (!data || data.order.status !== "pending") return;

    freeAttemptedRef.current = true;
    freeCheckoutMutation.mutate({ orderId, token });
  }, [data, freeCheckoutEnabled, orderId, token, freeCheckoutMutation]);

  const paymentUrl = (iyzicoMutation.data?.paymentPageUrl || data?.order.payment?.paymentPageUrl) ?? "";

  return (
    <div className="min-h-dvh bg-[#050508] text-slate-200">
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.14) 0%, rgba(10,10,12,0) 55%), radial-gradient(circle at 20% 25%, rgba(127,13,242,0.10) 0%, rgba(10,10,12,0) 55%)",
        }}
      />

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#050508]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4">
          <Link to="/numeroloji" className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d4af37] rotate-45">
              <span className="h-3 w-3 bg-[#d4af37]" />
            </span>
            <span className="font-display text-lg font-bold tracking-[0.16em] text-white">Numeroloji.app</span>
          </Link>

          <div className="flex items-center gap-6 sm:gap-8">
            <Link
              to="/numeroloji/magaza"
              className="font-display text-xs tracking-[0.22em] transition-colors hover:text-[rgba(212,175,55,0.95)] sm:text-sm"
            >
              MAĞAZA
            </Link>
            <Link to="/numeroloji/sepet" className="relative" aria-label="Sepet">
              <ShoppingBag className="h-5 w-5 text-slate-200" />
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="mb-10">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Ödeme</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            <Link to="/numeroloji" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Ana Sayfa
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <Link to="/numeroloji/sepet" className="transition-colors hover:text-[rgba(212,175,55,0.95)]">
              Sepetim
            </Link>
            <ChevronRight className="h-4 w-4 opacity-60" />
            <span style={{ color: GOLD }}>Ödeme</span>
          </div>
        </div>

        {!orderId || !token ? (
          <div className="rounded-2xl border p-6" style={{ borderColor: "rgba(212,175,55,0.12)", backgroundColor: "rgba(0,0,0,0.20)" }}>
            <div className="text-sm font-semibold text-white">Ödeme bağlantısı eksik</div>
            <div className="mt-1 text-sm text-slate-400">Bu sayfa satın alma adımından sonra açılmalıdır.</div>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <Button asChild className="rounded-full">
                <Link to="/numeroloji/magaza">Mağazaya git</Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full">
                <Link to="/numeroloji">Ana sayfa</Link>
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="rounded-3xl border p-6 shadow-2xl sm:p-8"
            style={{
              borderColor: "rgba(212,175,55,0.18)",
              background:
                "radial-gradient(circle at 70% 10%, rgba(212,175,55,0.12) 0%, transparent 45%), linear-gradient(145deg, rgba(26,18,37,0.55), rgba(10,10,12,0.72))",
            }}
          >
            {isLoading ? (
              <div className="text-sm text-slate-300">Yükleniyor...</div>
            ) : error ? (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                {(error as Error).message}
              </div>
            ) : data ? (
              <div className="grid gap-6">
                <div className="grid gap-2">
                  <div className="text-sm font-semibold text-white">Sipariş</div>
                  <div className="text-sm text-slate-400">#{data.order.id}</div>
                  <div className="text-sm text-slate-400">Toplam: {formatTry(data.order.totalTry)}</div>
                </div>

                {data.order.isDigital && (data.order.status === "paid" || data.order.status === "completed") ? (
                  <div className="rounded-2xl border border-[rgba(212,175,55,0.30)] bg-[rgba(212,175,55,0.06)] p-5">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 shrink-0 text-[#d4af37]" />
                      <div className="text-sm font-semibold text-[#d4af37]">Ödeme tamamlandı</div>
                    </div>
                    <div className="mt-2 text-sm text-slate-300">
                      {data.order.isMembership
                        ? <>Premium üyeliğin aktif edildi! <span className="font-semibold text-white">Soul Circles</span> uygulamasında hesabından çıkıp tekrar giriş yaparak tüm premium içeriklere erişebilirsin.</>
                        : <>Satın aldığın içeriğe <span className="font-semibold text-white">Soul Circles</span> uygulamasından erişebilirsin. Uygulama hesabın ile giriş yaptıktan sonra Ritueller sekmesinde içerik açık görünecektir.</>}
                    </div>
                  </div>
                ) : null}

                {data.order.status === "pending" && !freeCheckoutEnabled ? (
                  <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">İyzico ile ödeme</div>
                    <div className="mt-2 text-sm text-slate-400">
                      Ödeme için iyzico sayfasına yönlendirileceksin. Ödeme tamamlandıktan sonra bu sayfaya dönüp durumu kontrol edebilirsin.
                    </div>

                    {iyzicoMutation.isError ? (
                      <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                        {(iyzicoMutation.error as Error).message}
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button
                        variant="outline"
                        className="rounded-full border-[#d4af37] bg-black/60 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#d4af37]"
                        onClick={() => iyzicoMutation.mutate({ orderId, token })}
                        disabled={iyzicoMutation.isPending}
                      >
                        {iyzicoMutation.isPending ? "Ödeme başlatılıyor..." : tokenInvalid ? "Yeni ödeme başlat" : "İyzico ile öde"}
                      </Button>

                      {paymentUrl && !tokenInvalid ? (
                        <Button asChild variant="outline" className="rounded-full border-[#d4af37] bg-black/60 text-[#d4af37] hover:bg-[#d4af37]/10 hover:text-[#d4af37]">
                          <a href={paymentUrl} target="_self" rel="noreferrer">
                            Ödeme sayfasını tekrar aç
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {!(data.order.packageCount === 0 && data.order.productCount === 0) && (
                  <div className="rounded-2xl border border-[rgba(212,175,55,0.12)] bg-black/20 p-5">
                    <div className="text-sm font-semibold text-white">Durum</div>
                    <div className="mt-1 text-sm text-slate-400">
                      {data.order.status === "paid"
                        ? data.order.packageCount > 0
                          ? "Ödeme onaylandı. Bilgi formuna yönlendiriliyorsunuz..."
                          : "Ödeme onaylandı. Ürün siparişiniz alındı, analiz formu gerekmiyor."
                        : data.order.status === "pending" && freeCheckoutEnabled
                          ? "Ücretsiz test modu aktif. Siparişiniz otomatik olarak tamamlanıyor..."
                          : data.order.status === "pending"
                            ? "Ödeme bekleniyor. İyzico ile ödeme yaptıysan durumu kontrol et."
                            : "Sipariş iptal edildi."}
                    </div>

                    {data.order.status === "pending" && data.order.payment?.lastStatus ? (
                      <div className="mt-3 rounded-xl border border-[rgba(212,175,55,0.12)] bg-black/10 p-3 text-sm">
                        <div className="font-medium">Son ödeme sonucu: {data.order.payment.lastStatus}</div>
                        {data.order.payment.lastError ? <div className="mt-1 text-slate-400">{data.order.payment.lastError}</div> : null}
                      </div>
                    ) : null}

                    {iyzicoCheckMutation.isError ? (
                      <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                        {(iyzicoCheckMutation.error as Error).message}
                      </div>
                    ) : null}

                    {freeCheckoutMutation.isError ? (
                      <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                        {(freeCheckoutMutation.error as Error).message}
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      {!freeCheckoutEnabled ? (
                        <Button
                          variant="outline"
                          className="rounded-full border-white bg-white text-black hover:bg-white/90 hover:text-black"
                          onClick={() => iyzicoCheckMutation.mutate({ orderId, token })}
                          disabled={iyzicoCheckMutation.isPending || isFetching}
                        >
                          <RefreshCcw className="mr-2 h-4 w-4" />
                          {iyzicoCheckMutation.isPending || isFetching ? "Kontrol ediliyor..." : "Ödeme durumunu kontrol et"}
                        </Button>
                      ) : null}

                      {freeCheckoutEnabled && data.order.status === "pending" ? (
                        <Button
                          variant="outline"
                          className="rounded-full border-emerald-200/25 bg-emerald-200/10 text-emerald-50 hover:bg-emerald-200/15"
                          onClick={() => freeCheckoutMutation.mutate({ orderId, token })}
                          disabled={freeCheckoutMutation.isPending}
                        >
                          {freeCheckoutMutation.isPending ? "Ücretsiz tamamlanıyor..." : "Ücretsiz tamamla"}
                        </Button>
                      ) : null}

                      {data.order.packageCount > 0 ? (
                        <Button
                          className="rounded-full"
                          disabled={data.order.status !== "paid"}
                          onClick={() => {
                            const url = new URL("/numeroloji/form", window.location.origin);
                            url.searchParams.set("orderId", orderId);
                            url.searchParams.set("token", token);
                            if (packageKey) url.searchParams.set("package", packageKey);
                            url.searchParams.set("lang", reportLang);
                            navigate(url.pathname + url.search);
                          }}
                        >
                          Forma geç
                        </Button>
                      ) : null}
                    </div>

                    <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                      <ShieldCheck className="h-4 w-4" />
                      Güvenlik: Form oluşturma isteği, sipariş ödeme durumuna göre sunucu tarafından doğrulanır.
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}
      </main>

      <footer className="mt-20 border-t py-12" style={{ borderColor: "rgba(212,175,55,0.10)" }}>
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 px-4">
          <div className="h-24 w-px bg-gradient-to-b from-[rgba(212,175,55,0.95)] to-transparent" />
          <div className="text-center">
            <div className="font-display text-3xl tracking-widest" style={{ color: GOLD }}>
              Numeroloji.app
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.22em] opacity-40">
              Evrensel Sayılar Rehberi © {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
